import React, { Key, useCallback, useContext, useMemo, useState } from 'react';
import { RCMenu } from '@shared/components/RCMenu';
import { View } from '@shared/components/View';
import { MBIconV2 } from '@shared/components/image';
import { ArchiveRow } from '@shared/components/list';
import { TextInput } from '@shared/components/textInput';
import { MBTextBody1, MBTextOverlineBold } from '@shared/components/typography';
import { componentTemplateOptions } from '@shared/scenes/workflows/config';
import { useWorkflowDispatch } from '@shared/scenes/workflows/context/context';
import { OptionsData } from '@shared/scenes/workflows/template/options/OptionsData';
import { ColorThemeContext, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { useSetComponentField } from '../../hooks';
import { Divider } from './divider';

interface Props {
  component: MBAPI.WorkflowTemplate_components_StaticTextComponent;
}

export function StaticTextOptions({ component }: Props) {
  const colors = useContext(ColorThemeContext);
  const [openKeys, setOpenKeys] = useState<Key[]>([]);
  const [text, setText] = useSetComponentField<
    string,
    MBAPI.WorkflowTemplate_components_StaticTextComponent
  >(component, 'text');

  const optionsData = useMemo<OptionsData>(
    () => componentTemplateOptions.optionsDataForComponent(component),
    [component],
  );

  return (
    <View>
      <RCMenu
        mode="vertical"
        openKeys={openKeys as string[]}
        onOpenChange={setOpenKeys}
        triggerSubMenuAction="click"
        style={{ backgroundColor: 'white', borderRadius: grid(1), boxShadow: '0 0 10px #d9d9d9' }}
        expandIcon={null}
      >
        <View paddingHorizontal={3}>
          <MBTextOverlineBold color={colors.foregroundInactive}>
            {i18n('workflowTemplate.fieldType')}
          </MBTextOverlineBold>
        </View>
        <View paddingHorizontal={3} paddingVertical={2} row centerVertically>
          <MBIconV2 color={colors.foregroundActive} name={optionsData.icon} size={grid(3)} />
          <View paddingLeft={1} />
          <MBTextBody1 color={colors.foregroundActive}>{optionsData.title}</MBTextBody1>
        </View>
        <View paddingHorizontal={2}>
          <Divider />
        </View>
        <View paddingHorizontal={3} paddingVertical={1}>
          <MBTextOverlineBold color={colors.foregroundInactive}>
            {i18n('workflowTemplate.component.label.text')}
          </MBTextOverlineBold>
          <View padding={1} />
          <TextInput autosize={true} value={text} onChangeText={setText} />
        </View>
        <View paddingHorizontal={2}>
          <Divider />
        </View>
        <View paddingHorizontal={3} paddingVertical={1}>
          <MBTextOverlineBold color={colors.foregroundInactive}>
            {i18n('workflowTemplate.options')}
          </MBTextOverlineBold>
        </View>
        <ArchiveRow disabled={false} onArchive={useOnArchive(component.id)} />
      </RCMenu>
    </View>
  );
}

function useOnArchive(componentId: string) {
  const dispatch = useWorkflowDispatch();
  return useCallback(() => {
    dispatch({
      type: 'archiveComponent',
      args: componentId,
    });
  }, [componentId, dispatch]);
}
